import React from 'react';
import Typography from '@mui/material/Typography';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ButtonLoading from '@mui/lab/LoadingButton';
import { Autocomplete, Stack, TextField, Box, Alert, Avatar, Button, Chip } from '@mui/material';
import { toast } from 'react-toastify';
import {
  useEventDataSourceDisconnect,
  useGetEventDataSourceConfigDetails,
  useGetEventDataChannelListPairingColumns,
  useGetLastSyncHistory,
  useInsertEventDataSourceMapping,
} from 'services/v1/Tenant/AROEvent/EventDataSourceService';
import {
  EventDataChannel,
  IColumn,
  RequestMappingEventExternalDataSource,
} from 'types/api/Tenant/EventDataSourceTypes';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { SubjectItem } from 'types/api/SystemTenant/AROKMS/SubjectTypes';
import { PairsList } from './../PairListComponent';
import { ListColumn, PairListColumnRef } from './../ListColumnComponent';
import { DataSourcePreviewComponent } from './../DataSourcePreviewComponent';
import { ModalConfirmationComponent } from 'components/ModalComponent/ModalConfirmationComponent';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';

export type ModalMappingEventDataSourceProps = {
  onClose?: () => void;
  onBack?: () => void;
  isEdit: boolean;
  eventSubject?: SubjectItem;
  dataChannel: EventDataChannel;
};

export type PairItem = {
  sourceColumn: IColumn;
  targetColumn: IColumn;
};

const autocompleteStyles = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    padding: '1px 2px',
  },
  '& .MuiAutocomplete-popper': {
    fontSize: '10px',
  },
};
const textInputAutoCompleteStyles = {
  '& .MuiOutlinedInput': {
    padding: '1px 2px',
  },
};

const sectionCardStyles = {
  p: 2.5,
  borderRadius: 2,
  bgcolor: '#fff',
  border: '1px solid #E3EBF6',
};

export function PairingColumns(props: ModalMappingEventDataSourceProps) {
  const { onClose, onBack, eventSubject, isEdit, dataChannel } = props;

  const [selectedDataSourceTable, setSelectedDataSourceTable] = React.useState<AutoCompleteItem | null>(null);
  const [pairItemList, setPairItemList] = React.useState<PairItem[]>([]);
  const [pairKey, setPairKey] = React.useState<PairItem | null>(null);
  const [currentSelectedSourceColumn, setCurrentSelectedSourceColumn] = React.useState<IColumn | null>(null);
  const [currentSelectedTargetColumn, setCurrentSelectedTargetColumn] = React.useState<IColumn | null>(null);
  const [showDisconnectModal, setShowDisconnectModal] = React.useState<boolean>(false);
  const pairSourceColumnRef = React.useRef<PairListColumnRef>(null);
  const pairTargetColumnRef = React.useRef<PairListColumnRef>(null);
  const { data: eventDataSourceConfigDetails } = useGetEventDataSourceConfigDetails(
    isEdit ? eventSubject?.id?.toString() : undefined
  );
  const {
    data: listEventSourceColumns,
    isLoading: isLoadingEventSourceColumns,
    isSuccess: isEventSourceColumnsLoaded,
  } = useGetEventDataChannelListPairingColumns(eventSubject?.id?.toString(), dataChannel.id);
  const { mutate: disconnectEventDataSource } = useEventDataSourceDisconnect();
  const { remove: resetStatus } = useGetLastSyncHistory();
  const { mutate: insertEventDataSourceMapping, isLoading: isInserting } = useInsertEventDataSourceMapping();

  const sourceColumns = React.useMemo(() => {
    if (!listEventSourceColumns?.data.columnSource) return [];
    return listEventSourceColumns?.data?.columnSource?.filter((item) => {
      return !pairItemList.find((pair) => pair.sourceColumn.id === item.id);
    });
  }, [listEventSourceColumns?.data.columnSource, pairItemList]);

  const targetColumns = React.useMemo(() => {
    if (!listEventSourceColumns?.data.targetColumns) return [];
    return listEventSourceColumns?.data?.targetColumns?.filter((item) => {
      return !pairItemList.find((pair) => pair.targetColumn.id === item.id);
    });
  }, [listEventSourceColumns?.data.targetColumns, pairItemList]);

  const handleOnChangeSourceColumn = (value: IColumn) => {
    if (currentSelectedSourceColumn?.id === value.id) {
      setCurrentSelectedSourceColumn(null);
      return;
    }
    setCurrentSelectedSourceColumn(value);
  };

  const handleOnChangeTargetColumn = async (value: IColumn) => {
    if (currentSelectedTargetColumn?.id === value.id) {
      setCurrentSelectedTargetColumn(null);
      return;
    }
    setCurrentSelectedTargetColumn(value);
  };
  const handleOnMakePair = async () => {
    if (!currentSelectedSourceColumn && !currentSelectedTargetColumn) return;
    const newPairItem = {
      sourceColumn: currentSelectedSourceColumn,
      targetColumn: currentSelectedTargetColumn,
    };
    if (newPairItem.sourceColumn?.id && newPairItem.targetColumn?.id) {
      // @ts-ignore
      setPairItemList((oldPairItemList) => [...oldPairItemList, newPairItem]);
    }
    setCurrentSelectedSourceColumn(null);
    setCurrentSelectedTargetColumn(null);
    pairSourceColumnRef.current?.resetSearch();
    pairTargetColumnRef.current?.resetSearch();
  };

  React.useEffect(() => {
    if (currentSelectedSourceColumn && currentSelectedTargetColumn) {
      handleOnMakePair();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedSourceColumn, currentSelectedTargetColumn]);

  const handleOnUnpair = (value: PairItem) => {
    setPairItemList((oldPairItemList) => oldPairItemList.filter((item) => item !== value));
  };

  const handleOnSubmit = () => {
    if (eventSubject?.id && pairKey) {
      const payload: RequestMappingEventExternalDataSource = {
        pairItems: pairItemList,
        pairColumnKey: pairKey,
        dataChannelId: dataChannel.id,
        eventSubjectId: eventSubject.id.toString(),
      };
      insertEventDataSourceMapping(payload, {
        onSuccess: () => {
          handleOnClose();
        },
      });
    }
  };

  const handleOnDataSourceDisconnectClick = () => {
    if (eventSubject?.id) {
      disconnectEventDataSource(
        {
          eventSubjectId: eventSubject?.id?.toString(),
        },
        {
          onSuccess: () => {
            resetStatus();
            setShowDisconnectModal(false);
            handleOnClose();
            toast.success(`Event ${eventSubject?.displayName} data source disconnected successfully!`);
          },
        }
      );
    }
  };

  const handleOnClose = () => {
    onClose?.();
    setSelectedDataSourceTable(null);
    setPairItemList([]);
  };

  React.useEffect(() => {
    setCurrentSelectedSourceColumn(null);
    setCurrentSelectedTargetColumn(null);
  }, [selectedDataSourceTable]);

  React.useEffect(() => {
    if (eventDataSourceConfigDetails) {
      setPairItemList(eventDataSourceConfigDetails.data?.pairItemList || []);
      setPairKey(eventDataSourceConfigDetails.data?.pairingKey || null);
      setSelectedDataSourceTable(eventDataSourceConfigDetails?.data?.dataSourceTable);
    }
  }, [eventDataSourceConfigDetails]);

  const renderPairingKeyOptionItem = (props: React.HTMLAttributes<HTMLLIElement>, option: PairItem) => {
    const isSelected = pairKey?.sourceColumn?.id === option.sourceColumn.id && pairKey?.targetColumn?.id === option.targetColumn.id;
    return (
      <Box
        component='li'
        {...props}
        sx={{
          '&.MuiAutocomplete-option': {
            borderLeft: isSelected ? '3px solid #42BB93' : '3px solid transparent',
            bgcolor: isSelected ? '#EEF7F3' : 'transparent',
          },
        }}
      >
        <Stack direction='row' alignItems='center' gap={1.5} py={0.5} width='100%'>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '6px',
              bgcolor: isSelected ? '#42BB93' : '#EDF0FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <VpnKeyOutlinedIcon sx={{ fontSize: 14, color: isSelected ? '#fff' : '#3B4797' }} />
          </Box>
          <Stack flex={1} spacing={0}>
            <Stack direction='row' alignItems='center' gap={1}>
              <Typography variant='body2' fontSize={13} fontWeight={700} sx={{ color: '#141414' }}>
                {option.sourceColumn.columnName}
              </Typography>
              <CompareArrowsOutlinedIcon sx={{ color: '#42BB93', fontSize: '16px !important' }} />
              <Typography variant='body2' fontSize={13} fontWeight={700} sx={{ color: '#3B4797' }}>
                {option.targetColumn.columnName}
              </Typography>
            </Stack>
            <Typography variant='input-label-gray' fontSize={11}>
              Match records by <b>{option.sourceColumn.columnName}</b>
            </Typography>
          </Stack>
        </Stack>
      </Box>
    );
  };

  const getSourceColumnsWarningMessage = () => {
    return targetColumns.length !== 0
      ? "All items in this list must be paired with an item in the 'Source Columns' list."
      : undefined;
  };

  return (
    <Stack sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Scrollable content */}
      <Stack sx={{ flex: 1, overflow: 'auto', bgcolor: '#F8F9FC' }}>
        <Stack px={4} py={3} gap={3} sx={{ maxWidth: 1400, mx: 'auto', width: '100%' }}>
          {isLoadingEventSourceColumns ? (
            <Stack sx={{ height: 400 }}>
              <ActivityIndicator />
            </Stack>
          ) : (
            <>
              {/* Channel info banner */}
              <Stack sx={sectionCardStyles}>
                <Stack direction='row' alignItems='center' gap={2.5}>
                  {dataChannel.logoUrl && (
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        bgcolor: '#F8F9FC',
                        border: '1px solid #E3EBF6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Avatar
                        variant='square'
                        alt={dataChannel.name}
                        src={dataChannel.logoUrl}
                        imgProps={{ style: { objectFit: 'inherit' } }}
                        sx={{ width: 140, height: 36, objectFit: 'contain' }}
                      />
                    </Box>
                  )}
                  <Stack spacing={0.3}>
                    <Typography variant='input-label' fontSize={16} fontWeight={800}>
                      {dataChannel.name}
                    </Typography>
                    <Typography variant='input-label-gray' fontSize={13} sx={{ lineHeight: 1.4 }}>
                      {dataChannel.description}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>

              {isEventSourceColumnsLoaded && (
                <>
                  {/* Preview Data section */}
                  <Stack sx={sectionCardStyles} gap={1}>
                    <Stack>
                      <Typography variant='input-label' fontSize={15} fontWeight={800}>
                        Preview Data
                      </Typography>
                      <Typography variant='input-label-gray' fontSize={13}>
                        Explore available data with a preview from {dataChannel.name}.
                      </Typography>
                    </Stack>
                    <DataSourcePreviewComponent dataChannelId={dataChannel.id} />
                  </Stack>

                  {/* Column Pairing section */}
                  <Stack sx={sectionCardStyles} gap={2}>
                    <Stack>
                      <Typography variant='input-label' fontSize={15} fontWeight={800}>
                        Column Pairing
                      </Typography>
                      <Typography variant='input-label-gray' fontSize={13}>
                        Connect and integrate by pairing columns between{' '}
                        <Box component='span' fontWeight={600} sx={{ color: '#141414' }}>
                          {dataChannel.name}
                        </Box>{' '}
                        and{' '}
                        <Box component='span' fontWeight={600} sx={{ color: '#3B4797' }}>
                          {eventSubject?.displayName}
                        </Box>
                        . Select one column from each side to automatically pair them.
                      </Typography>
                    </Stack>
                    <Stack direction='row' justifyContent='space-between' width='100%' gap={2}>
                      <ListColumn
                        title='Target Columns'
                        warningMessage={getSourceColumnsWarningMessage()}
                        otherPairValue={currentSelectedSourceColumn}
                        items={targetColumns}
                        subheader={`${targetColumns.length} unpaired columns`}
                        value={currentSelectedTargetColumn}
                        ref={pairTargetColumnRef}
                        onChange={handleOnChangeTargetColumn}
                      />
                      <ListColumn
                        title='Source Columns'
                        otherPairValue={currentSelectedTargetColumn}
                        items={sourceColumns}
                        value={currentSelectedSourceColumn}
                        ref={pairSourceColumnRef}
                        subheader={`${sourceColumns.length} unpaired columns`}
                        onChange={handleOnChangeSourceColumn}
                      />
                      <PairsList items={pairItemList} title='Paired Columns' subheader={`${pairItemList.length} pairs mapped`} onUnpair={handleOnUnpair} />
                    </Stack>
                  </Stack>

                  {/* Pairing Key section */}
                  <Stack sx={sectionCardStyles} gap={2}>
                    {/* Header row */}
                    <Stack direction='row' alignItems='center' gap={1.5}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '10px',
                          bgcolor: pairKey ? '#EEF7F3' : '#EDF0FF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        <VpnKeyOutlinedIcon sx={{ color: pairKey ? '#42BB93' : '#3B4797', fontSize: 18 }} />
                      </Box>
                      <Stack>
                        <Typography variant='input-label' fontSize={15} fontWeight={800}>
                          Select Pairing Key
                        </Typography>
                        <Typography variant='input-label-gray' fontSize={13}>
                          Choose a column pair that uniquely identifies records for matching during synchronization.
                        </Typography>
                      </Stack>
                    </Stack>

                    {pairItemList.length === 0 ? (
                      <Alert
                        severity='info'
                        variant='outlined'
                        sx={{
                          borderRadius: 1.5,
                          bgcolor: '#F8FAFF',
                          '& .MuiAlert-message': { fontSize: 13 },
                        }}
                      >
                        Pair at least one column above before selecting a pairing key.
                      </Alert>
                    ) : (
                      <Stack direction='row' gap={2.5} alignItems='flex-start'>
                        {/* Autocomplete selector */}
                        <Stack flex={1} gap={1}>
                          <Typography variant='input-label-gray' fontSize={12} fontWeight={600}>
                            Choose from paired columns
                          </Typography>
                          <Autocomplete
                            sx={{
                              ...autocompleteStyles,
                              '& .MuiOutlinedInput-root': {
                                padding: '4px 8px',
                                borderRadius: 1.5,
                              },
                            }}
                            options={pairItemList || []}
                            clearIcon={null}
                            getOptionLabel={(option) =>
                              `${option.sourceColumn.columnName} - ${option.targetColumn.columnName}`
                            }
                            value={pairKey}
                            onChange={(event, value) => {
                              setPairKey(value);
                            }}
                            renderOption={renderPairingKeyOptionItem}
                            renderInput={(params) => {
                              return (
                                <TextField
                                  {...params}
                                  sx={textInputAutoCompleteStyles}
                                  value={`${params}`}
                                  placeholder='Select pairing key...'
                                />
                              );
                            }}
                          />
                        </Stack>

                        {/* Selected key preview card */}
                        <Stack
                          flex={1}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: pairKey ? '1px solid #C5E8D9' : '1px dashed #D0D5DD',
                            bgcolor: pairKey ? '#F0FAF6' : '#FAFBFD',
                            transition: 'all 0.2s ease',
                            minHeight: 72,
                            justifyContent: 'center',
                          }}
                        >
                          {pairKey ? (
                            <Stack gap={1}>
                              <Stack direction='row' alignItems='center' gap={1}>
                                <VpnKeyOutlinedIcon sx={{ fontSize: 15, color: '#42BB93' }} />
                                <Typography fontSize={12} fontWeight={700} sx={{ color: '#42BB93', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                  Active Pairing Key
                                </Typography>
                              </Stack>
                              <Stack direction='row' alignItems='center' gap={1} ml={0.5}>
                                <Chip
                                  label={pairKey.sourceColumn.columnName}
                                  size='small'
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: 12,
                                    bgcolor: '#fff',
                                    border: '1px solid #C5E8D9',
                                    color: '#141414',
                                  }}
                                />
                                <CompareArrowsOutlinedIcon sx={{ color: '#42BB93', fontSize: 18 }} />
                                <Chip
                                  label={pairKey.targetColumn.columnName}
                                  size='small'
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: 12,
                                    bgcolor: '#fff',
                                    border: '1px solid #C7CEED',
                                    color: '#3B4797',
                                  }}
                                />
                              </Stack>
                              <Typography variant='input-label-gray' fontSize={11} ml={0.5}>
                                Records will be matched using the <b>{pairKey.sourceColumn.columnName}</b> column from{' '}
                                {dataChannel.name}.
                              </Typography>
                            </Stack>
                          ) : (
                            <Stack alignItems='center' gap={0.5} py={0.5}>
                              <VpnKeyOutlinedIcon sx={{ fontSize: 20, color: '#C0C7D0' }} />
                              <Typography fontSize={12} sx={{ color: '#98A2AE' }}>
                                No pairing key selected
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                      </Stack>
                    )}
                  </Stack>
                </>
              )}
            </>
          )}
        </Stack>
      </Stack>

      {/* Sticky footer action bar */}
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        px={4}
        py={1.5}
        sx={{
          borderTop: '1px solid #E3EBF6',
          bgcolor: '#FAFBFD',
          flexShrink: 0,
        }}
      >
        <Stack direction='row' gap={1}>
          {onBack && (
            <Button
              variant='main-table-panel-border'
              startIcon={<ArrowBackOutlinedIcon />}
              onClick={onBack}
              disabled={isInserting}
            >
              Back
            </Button>
          )}
          {isEdit && (
            <ButtonLoading
              sx={{ textTransform: 'none' }}
              variant='outlined'
              onClick={() => setShowDisconnectModal(true)}
              color='error'
              disabled={isInserting}
            >
              Disconnect
            </ButtonLoading>
          )}
        </Stack>
        <Stack direction='row' gap={1.5}>
          <ButtonLoading variant='main-table-panel-border' disabled={isInserting} onClick={handleOnClose}>
            Cancel
          </ButtonLoading>
          <ButtonLoading
            variant='main-table-panel'
            onClick={handleOnSubmit}
            disabled={!pairKey || !dataChannel.id || !eventSubject?.id}
            loading={isInserting}
          >
            {isEdit ? 'Update Connection' : 'Connect Channel'}
          </ButtonLoading>
        </Stack>
      </Stack>

      <ModalConfirmationComponent
        visible={showDisconnectModal}
        title={`Disconnect Data Channel from ${eventSubject?.displayName}`}
        width={600}
        onApprove={handleOnDataSourceDisconnectClick}
        onCancel={() => setShowDisconnectModal(false)}
        onClose={() => setShowDisconnectModal(false)}
        message={`Disconnecting the data channel from ${eventSubject?.displayName} will halt daily data synchronization.
              \n All previously synchronized data will remain intact and accessible within the ${eventSubject?.displayName}. You'll have the option to manually edit or delete the data.
              \n Are you sure you want to proceed with disconnecting the ${dataChannel.name} from the ${eventSubject?.displayName}?`}
        labelButtonApprove='Disconnect'
      />
    </Stack>
  );
}
